import { Injectable, inject } from '@angular/core';
import { Observable, of, from, map } from 'rxjs';
import { HttpService } from '@core/services/http.service';
import { Api } from '../../../../api/gse/api';
import {
  getBackJobReport,
  GetBackJobReport$Params,
} from '../../../../api/gse/fn/maintenance-report/get-back-job-report';
import {
  getEquipmentServiceabilityReport,
  GetEquipmentServiceabilityReport$Params,
} from '../../../../api/gse/fn/maintenance-report/get-equipment-serviceability-report';
import {
  getPmComplianceReport,
  GetPmComplianceReport$Params,
} from '../../../../api/gse/fn/maintenance-report/get-pm-compliance-report';
import {
  getInventorySparePartReport,
  GetInventorySparePartReport$Params,
} from '../../../../api/gse/fn/inventory-report/get-inventory-spare-part-report';
import { GetBackJobReportResponse } from '../../../../api/gse/models/get-back-job-report-response';
import { GetEquipmentServiceabilityReportResponse } from '../../../../api/gse/models/get-equipment-serviceability-report-response';
import { GetPmComplianceReportResponse } from '../../../../api/gse/models/get-pm-compliance-report-response';
import { GetInventorySparePartReportResponse } from '../../../../api/gse/models/get-inventory-spare-part-report-response';
import {
  getInventoryMaintenanceHistoryReport,
  GetInventoryMaintenanceHistoryReport$Params,
} from '../../../../api/gse/fn/inventory-report/get-inventory-maintenance-history-report';
import {
  getInventoryProcurementReport,
  GetInventoryProcurementReport$Params,
} from '../../../../api/gse/fn/inventory-report/get-inventory-procurement-report';
import { GetInventoryMaintenanceHistoryReportResponse } from '../../../../api/gse/models/get-inventory-maintenance-history-report-response';
import { GetInventoryProcurementReportResponse } from '../../../../api/gse/models/get-inventory-procurement-report-response';
import {
  getTechnicianEfficiencyReport,
  GetTechnicianEfficiencyReport$Params,
} from '../../../../api/gse/fn/maintenance-report/get-technician-efficiency-report';
import { GetTechnicianEfficiencyReportResponse } from '../../../../api/gse/models/get-technician-efficiency-report-response';
import {
  getJobCardTatReport,
  GetJobCardTatReport$Params,
} from '../../../../api/gse/fn/maintenance-report/get-job-card-tat-report';
import { GetJobCardTatReportResponse } from '../../../../api/gse/models/get-job-card-tat-report-response';
import {
  getEquipmentDowntimeReport,
  GetEquipmentDowntimeReport$Params,
} from '../../../../api/gse/fn/maintenance-report/get-equipment-downtime-report';
import { GetEquipmentDowntimeReportResponse } from '../../../../api/gse/models/get-equipment-downtime-report-response';
import { GetEquipmentTatReportResponse } from '../../../../api/gse/models/get-equipment-tat-report-response';
import { JobCardStatus } from '../../../../api/gse/models/job-card-status';
import { EquipmentType } from '../../../../api/gse/models/equipment-type';
import { EquipmentStatus } from '../../../../api/gse/models/equipment-status';
import { ServiceType } from '../../../../api/gse/models/service-type';
import { Priority } from '../../../../api/gse/models/priority';
import { getEquipmentTatReport, GetEquipmentTatReport$Params } from 'src/app/api/gse/functions';
import { Api as AdministrationApi } from '../../../../api/administration/api';
import {
  getDelegationReport,
  GetDelegationReport$Params,
} from '../../../../api/administration/fn/delegation-report/get-delegation-report';
import { GetDelegationReportResponse } from '../../../../api/administration/models/get-delegation-report-response';
import { DelegationStatus } from '../../../../api/administration/models/delegation-status';
import {
  getUserReport,
  GetUserReport$Params,
} from '../../../../api/administration/fn/users-report/get-user-report';
import { GetUserReportResponse } from '../../../../api/administration/models/get-user-report-response';
import { UserStatus } from '../../../../api/administration/models/user-status';

export interface DropdownOption {
  label: string;
  value: string | number;
}

export interface ReportFilterOptions {
  manufacturers?: DropdownOption[];
  models?: DropdownOption[];
  equipmentIds?: DropdownOption[];
  technicians?: DropdownOption[];
  categories?: DropdownOption[];
  brands?: DropdownOption[];
  serviceTypes?: DropdownOption[];
  [key: string]: DropdownOption[] | undefined;
}

export interface ReportDataParams {
  page: number;
  size: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  filters?: Record<string, string | number | boolean | string[] | number[] | null | undefined>;
}

export type HttpQueryParams = Record<
  string,
  string | number | boolean | ReadonlyArray<string | number | boolean>
>;

@Injectable({
  providedIn: 'root',
})
export class ReportDataService {
  private httpService = inject(HttpService);
  private api = inject(Api);
  private administrationApi = inject(AdministrationApi);

  getSparePartsData(params: ReportDataParams): Observable<GetInventorySparePartReportResponse> {
    const apiParams: GetInventorySparePartReport$Params = {
      page: params.page,
      size: params.size,
      sort: 'createdAt,desc',
      search: params.search,
      startDate: params.startDate,
      endDate: params.endDate,
    };

    if (params.filters) {
      const catValue = params.filters['sparePartCategory'];
      if (catValue) {
        apiParams.categoryIds = (Array.isArray(catValue) ? catValue : [catValue]).map((v) =>
          Number(v)
        );
      }

      const brandValue = params.filters['sparePartBrand'];
      if (brandValue) {
        apiParams.brandIds = (Array.isArray(brandValue) ? brandValue : [brandValue]).map((v) =>
          Number(v)
        );
      }
    }

    return from(this.api.invoke(getInventorySparePartReport, apiParams));
  }

  getEquipmentTATData(params: ReportDataParams): Observable<GetEquipmentTatReportResponse> {
    const apiParams: GetEquipmentTatReport$Params = {
      page: params.page,
      size: params.size,
      sort: 'createdAt,desc',
    };

    if (params.search) apiParams.search = params.search;
    if (params.startDate) apiParams.startDate = params.startDate;
    if (params.endDate) apiParams.endDate = params.endDate;

    if (params.filters) {
      if (params.filters['equipmentId']) {
        const val = params.filters['equipmentId'];
        apiParams.equipmentIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['manufacturer']) {
        const val = params.filters['manufacturer'];
        apiParams.manufacturerIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['equipmentModel']) {
        const val = params.filters['equipmentModel'];
        apiParams.modelIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
    }

    return from(this.api.invoke(getEquipmentTatReport, apiParams));
  }

  getMaintenanceHistoryData(
    params: ReportDataParams
  ): Observable<GetInventoryMaintenanceHistoryReportResponse> {
    const apiParams: GetInventoryMaintenanceHistoryReport$Params = {
      page: params.page,
      size: params.size,
      sort: 'createdAt,desc',
      search: params.search,
      startDate: params.startDate,
      endDate: params.endDate,
    };

    if (params.filters) {
      if (params.filters['equipment']) {
        const val = params.filters['equipment'];
        apiParams.equipmentIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['technician']) {
        const val = params.filters['technician'];
        apiParams.technicianIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['maintenanceType']) {
        const val = params.filters['maintenanceType'];
        const selected = Array.isArray(val) ? val : [val];
        const preventiveTypes: ServiceType[] = [
          'PM_REGULAR',
          'PM_LOOP',
          'PM_SCHEDULED',
          'PM_TUV_RENEWAL',
          'PM_RAMP_PASS_RENEWAL',
          'PM_MAJOR',
          'PM_MINOR',
        ];
        const expanded: ServiceType[] = [];
        for (const v of selected) {
          if (v === 'PREVENTIVE_MAINTENANCE') {
            expanded.push(...preventiveTypes);
          } else {
            expanded.push(v as ServiceType);
          }
        }
        apiParams.maintenanceTypes = expanded;
      }
    }

    return from(this.api.invoke(getInventoryMaintenanceHistoryReport, apiParams));
  }

  getProcurementData(params: ReportDataParams): Observable<GetInventoryProcurementReportResponse> {
    const apiParams: GetInventoryProcurementReport$Params = {
      page: params.page,
      size: params.size,
      sort: 'createdAt,desc',
      search: params.search,
      startDate: params.startDate,
      endDate: params.endDate,
    };

    if (params.filters) {
      if (params.filters['pettyCash'] !== undefined) {
        const val = params.filters['pettyCash'];
        apiParams.pettyCashEnabled = Array.isArray(val)
          ? (val as string[]).includes('true')
          : val === 'true';
      }
    }

    return from(this.api.invoke(getInventoryProcurementReport, apiParams));
  }

  getJobCardSummaryData(params: ReportDataParams): Observable<GetJobCardTatReportResponse> {
    const apiParams: GetJobCardTatReport$Params = {
      page: params.page,
      size: params.size,
      sort: 'createdAt,desc',
    };

    if (params.search) apiParams.search = params.search;
    if (params.startDate) apiParams.startDate = params.startDate;
    if (params.endDate) apiParams.endDate = params.endDate;

    if (params.filters) {
      if (params.filters['equipment']) {
        const val = params.filters['equipment'];
        apiParams.equipmentIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['jobCardStatus']) {
        const val = params.filters['jobCardStatus'];
        apiParams.status = String(Array.isArray(val) ? val[0] : val) as JobCardStatus;
      }
      if (params.filters['priority']) {
        const val = params.filters['priority'];
        apiParams.priority = String(Array.isArray(val) ? val[0] : val) as Priority;
      }
    }

    return from(this.api.invoke(getJobCardTatReport, apiParams));
  }

  getBackJobData(params: ReportDataParams): Observable<GetBackJobReportResponse> {
    const apiParams: GetBackJobReport$Params = {
      page: params.page,
      size: params.size,
      sort: 'createdAt,desc',
    };

    if (params.search) apiParams.search = params.search;
    if (params.startDate) apiParams.startDate = params.startDate;
    if (params.endDate) apiParams.endDate = params.endDate;
    if (params.filters) {
      if (params.filters['equipment']) {
        const val = params.filters['equipment'];
        apiParams.equipmentIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['backJobStatus']) {
        const val = params.filters['backJobStatus'];
        apiParams.status = (Array.isArray(val) ? val : [val]) as JobCardStatus[];
      }
    }

    return from(this.api.invoke(getBackJobReport, apiParams));
  }

  getTechnicianEfficiencyData(
    params: ReportDataParams
  ): Observable<GetTechnicianEfficiencyReportResponse> {
    const apiParams: GetTechnicianEfficiencyReport$Params = {
      page: params.page,
      size: params.size,
      sort: 'createdAt,desc',
      search: params.search,
      startDate: params.startDate,
      endDate: params.endDate,
    };

    if (params.filters) {
      if (params.filters['technician']) {
        const val = params.filters['technician'];
        apiParams.technicianIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
    }

    return from(this.api.invoke(getTechnicianEfficiencyReport, apiParams));
  }

  getEquipmentServiceabilityData(
    params: ReportDataParams
  ): Observable<GetEquipmentServiceabilityReportResponse> {
    const apiParams: GetEquipmentServiceabilityReport$Params = {
      page: params.page,
      size: params.size,
      sort: 'createdAt,desc',
      search: params.search,
      startDate: params.startDate,
      endDate: params.endDate,
    };

    if (params.filters) {
      if (params.filters['equipment']) {
        const val = params.filters['equipment'];
        apiParams.equipmentIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['equipmentCategory']) {
        const val = params.filters['equipmentCategory'];
        apiParams.categoryIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['equipmentType']) {
        const val = params.filters['equipmentType'];
        apiParams.type = (Array.isArray(val) ? val : [val]) as EquipmentType[];
      }
      if (params.filters['equipmentServiceability']) {
        const val = params.filters['equipmentServiceability'];
        apiParams.serviceability = (Array.isArray(val) ? val : [val]) as EquipmentStatus[];
      }
      if (params.filters['equipmentModel']) {
        const val = params.filters['equipmentModel'];
        apiParams.modelIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
    }

    return from(this.api.invoke(getEquipmentServiceabilityReport, apiParams));
  }

  getEquipmentDowntimeData(
    params: ReportDataParams
  ): Observable<GetEquipmentDowntimeReportResponse> {
    const apiParams: GetEquipmentDowntimeReport$Params = {
      page: params.page,
      size: params.size,
      sort: 'createdAt,desc',
    };

    if (params.search) apiParams.search = params.search;
    if (params.startDate) apiParams.startDate = params.startDate;
    if (params.endDate) apiParams.endDate = params.endDate;

    if (params.filters) {
      if (params.filters['equipment']) {
        const val = params.filters['equipment'];
        apiParams.equipmentIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
    }

    return from(this.api.invoke(getEquipmentDowntimeReport, apiParams));
  }

  getPMComplianceData(params: ReportDataParams): Observable<GetPmComplianceReportResponse> {
    const apiParams: GetPmComplianceReport$Params = {
      page: params.page,
      size: params.size,
      sort: 'createdAt,desc',
    };

    if (params.search) apiParams.search = params.search;
    if (params.startDate) apiParams.startDate = params.startDate;
    if (params.endDate) apiParams.endDate = params.endDate;
    if (params.filters?.['equipment']) {
      const val = params.filters['equipment'];
      apiParams.equipmentIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
    }

    return from(this.api.invoke(getPmComplianceReport, apiParams));
  }

  getDelegationData(params: ReportDataParams): Observable<GetDelegationReportResponse> {
    const apiParams: GetDelegationReport$Params = {
      page: params.page,
      size: params.size,
      sort: 'createdAt,desc',
    };

    if (params.search) apiParams.search = params.search;
    if (params.startDate) apiParams.startDate = params.startDate;
    if (params.endDate) apiParams.endDate = params.endDate;

    if (params.filters) {
      if (params.filters['delegator']) {
        const val = params.filters['delegator'];
        apiParams.delegator = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['delegate']) {
        const val = params.filters['delegate'];
        apiParams.delegate = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['delegationStatus']) {
        const val = params.filters['delegationStatus'];
        apiParams.status = (Array.isArray(val) ? val : [val]) as DelegationStatus[];
      }
    }

    return from(this.administrationApi.invoke(getDelegationReport, apiParams));
  }

  getUserMasterListData(params: ReportDataParams): Observable<GetUserReportResponse> {
    const apiParams: GetUserReport$Params = {
      page: params.page,
      size: params.size,
      sort: 'createdAt,desc',
    };

    if (params.search) apiParams.search = params.search;

    if (params.filters) {
      if (params.filters['userStatus']) {
        const val = params.filters['userStatus'];
        apiParams.status = (Array.isArray(val) ? val : [val]) as UserStatus[];
      }
    }

    return from(this.administrationApi.invoke(getUserReport, apiParams));
  }

  exportToExcel(reportType: string, params: ReportDataParams): Observable<Blob> {
    const queryParams: HttpQueryParams = {};

    if (params.search) queryParams['search'] = params.search;
    if (params.startDate) queryParams['startDate'] = params.startDate;
    if (params.endDate) queryParams['endDate'] = params.endDate;

    if (params.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          queryParams[key] = value as
            | string
            | number
            | boolean
            | ReadonlyArray<string | number | boolean>;
        }
      });
    }

    return this.httpService.get(`reports/${reportType}/export`, queryParams);
  }
}
